import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootStore } from '../../redux/store'
import MyActionsPageHeader from '../../shared/UI/MyActionsPageHeader'
import InstanceStatusBanner from '../../shared/UI/InstanceStatusBanner'
import DurationCard from './DurationCard'
import SummaryCard from './SummaryCard'
import EditCard from './EditCard'
import AbsenceDetailCard from './AbsenceDetailCard'
import { useManAuthData } from './hooks'
import OccurrenceSubNav from '../../shared/UI/OccurrenceSubNav'
import { AbsenceDetailsResponse, AbsenceOccurrence } from '../../types/absence'
import UserErrorMessage from '../../utils/errorFilter'
import { BaseResponse } from '../../types/base-response'
import { manualRequestsService } from '../../services/myActionsService'
import { showErrorMessage } from '../../redux/reducers/snackbarReducer'
import AttendanceCalendar from '../AttendanceCalendar'
import { isSickness } from '../Absence/Actions/ActionsHelperFunctions/actionsHelpers'

export default function ManAuthPage() {
  const dispatch = useDispatch()
  const { id } = useParams<{ id: string }>()
  const {
    manAuthRequest,
    isLoading,
    comments,
    setComments,
    approverComments,
    setApproverComments,
    hours,
    setHours,
    isHoursChanged,
    areFieldsChanged,
    isSubmitting,
    maxAllowedHours,
    rows,
    handleRequestUpdate,
  } = useManAuthData(id)

  const { employeeDetails } = useSelector((state: RootStore) => state.appSettings)
  const isManager = manAuthRequest && employeeDetails?.employeeId === manAuthRequest.createdById
  const [absenceOccurrence, setAbsenceOccurrence] = useState<AbsenceOccurrence>()
  const [view, setView] = useState('Overview')
  const [hasValidRtw, setHasValidRtw] = useState<boolean>(false)
  const navigate = useNavigate()
  const location = useLocation()

  const activeRoute = useSelector<RootStore, string>(
    (state: RootStore) => state.appSettings.activeRoute
  )

  const getAbsenceData = useCallback(() => {
    if (!manAuthRequest?.absenceId) return

    manualRequestsService
      .getAbsenceOccurrence(Number(manAuthRequest.absenceId))
      .then((result: AbsenceDetailsResponse) => {
        setAbsenceOccurrence(result.absenceOccurrence)
        setHasValidRtw(result.absenceOccurrence.hasValidRtw)
      })
      .catch(err => {
        const returnTo = location.state?.previousLocation || '/myactions'
        navigate(returnTo)
        const response: BaseResponse = err.response.data
        response.errors.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })
  }, [manAuthRequest?.absenceId, dispatch, navigate, activeRoute])

  const subNav = [
    {
      label: 'Overview',
      onClick: () => setView('Overview'),
    },
    {
      label: 'Calendar',
      onClick: () => setView('Calendar'),
    }
  ]

  useEffect(() => {
    if (manAuthRequest?.absenceId) {
      getAbsenceData()
    }
  }, [manAuthRequest?.absenceId])

  return (
    <>
      <MyActionsPageHeader title="Discretionary Pay" />
      
      {!isLoading && manAuthRequest && (
        <>
          <Grid container spacing={4} mb={3}>
            <Grid item xs={12} md={8}>
              <InstanceStatusBanner
                showBackButton
                displayName={manAuthRequest.displayName}
                status={
                  manAuthRequest.requestStatus === null ? undefined : manAuthRequest.requestStatus
                }
                requestId={manAuthRequest.id}
              />
            </Grid>
            <Grid item xs={12} md={4} display="flex" justifyContent="flex-end" alignItems="center">
              <OccurrenceSubNav navItems={subNav} />
            </Grid>
          </Grid>
          
          {/* Main Content Area */}
          {view === 'Calendar' && absenceOccurrence && (
            <AttendanceCalendar attendanceEvent={absenceOccurrence} />
          )}

          {/* Only show this content if view is NOT Calendar */}
          {view !== 'Calendar' && (
            <>
              {/* Main Content Area - Top Half */}
              <Grid container item xs={12} spacing={4}>
                {/* Left Column - Cards Stacked */}
                <Grid item xs={12} lg={6}>
                  <Grid container spacing={4} direction="column">
                    {/* Duration Card */}
                    <Grid item>
                      <DurationCard manAuthRequest={manAuthRequest} />
                    </Grid>

                    {/* Summary Card */}
                    <Grid item>
                      <SummaryCard manAuthRequest={manAuthRequest} isManager={isManager} />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Right Column - Approval Card */}
                <Grid item xs={12} lg={6}>
                  <Grid item>
                    <EditCard
                      isManager={isManager}
                      hours={hours}
                      setHours={setHours}
                      comments={comments}
                      setComments={setComments}
                      approverComments={approverComments}
                      setApproverComments={setApproverComments}
                      maxAllowedHours={maxAllowedHours}
                      isHoursChanged={isHoursChanged}
                      areFieldsChanged={areFieldsChanged}
                      isSubmitting={isSubmitting}
                      manAuthRequest={manAuthRequest}
                      handleRequestUpdate={handleRequestUpdate}
                      hasValidRtw={hasValidRtw}
                    />
                  </Grid>
                </Grid>
              </Grid>
              {absenceOccurrence && (
                <Grid item xs={12} mt={3}>
                  <AbsenceDetailCard
                    absenceDays={absenceOccurrence.absenceDays ?? []}
                    rows={rows}
                    isSickness={isSickness(absenceOccurrence)}
                    locationGroupName={absenceOccurrence.employeeResponse?.locationGroupName}
                  />
                </Grid>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}
